import React from "react";
import { Modal } from "./Modal";
import { Button } from "./Button";
import { AlertTriangle } from "lucide-react";

interface ConfirmationModalProps {
  /** Controls whether the modal is visible */
  open: boolean;
  /** Called when clicking cancel or close */
  onClose: () => void;
  /** Called when clicking confirm */
  onConfirm: () => void;
  /** Modal title */
  title: string;
  /** Confirmation message */
  message: string;
  /** Confirm button text (default: "Confirmar") */
  confirmText?: string;
  /** Cancel button text (default: "Cancelar") */
  cancelText?: string;
  /** Whether the action is loading/pending */
  isLoading?: boolean;
  /** Type of confirmation (affects styling) */
  variant?: "danger" | "warning" | "info";
}

/**
 * Reusable confirmation modal for destructive or important actions.
 *
 * Features:
 * - Visual indicator based on variant (danger, warning, info)
 * - Disabled confirm button during loading
 * - Keyboard support (ESC to cancel)
 * - Accessible with proper ARIA labels
 */
export const ConfirmationModal: React.FC<ConfirmationModalProps> = ({
  open,
  onClose,
  onConfirm,
  title,
  message,
  confirmText = "Confirmar",
  cancelText = "Cancelar",
  isLoading = false,
  variant = "danger",
}) => {
  const handleConfirm = () => {
    onConfirm();
  };

  const getIconColor = () => {
    switch (variant) {
      case "danger":
        return "text-red-600";
      case "warning":
        return "text-yellow-600";
      case "info":
        return "text-blue-600";
      default:
        return "text-red-600";
    }
  };

  const getConfirmButtonClass = () => {
    switch (variant) {
      case "danger":
        return "bg-red-600 hover:bg-red-700 text-white";
      case "warning":
        return "bg-yellow-600 hover:bg-yellow-700 text-white";
      case "info":
        return "bg-blue-600 hover:bg-blue-700 text-white";
      default:
        return "bg-red-600 hover:bg-red-700 text-white";
    }
  };

  return (
    <Modal open={open} onClose={onClose} title={title} size="sm">
      <div className="flex flex-col gap-4">
        {/* Icon and Message */}
        <div className="flex items-start gap-3">
          <div className={`shrink-0 ${getIconColor()}`}>
            <AlertTriangle className="h-6 w-6" />
          </div>
          <p className="text-sm text-gray-700 leading-relaxed">{message}</p>
        </div>

        {/* Action Buttons */}
        <div className="flex flex-col-reverse sm:flex-row gap-2 sm:justify-end">
          <Button
            variant="outline"
            onClick={onClose}
            disabled={isLoading}
            className="w-full sm:w-auto"
          >
            {cancelText}
          </Button>
          <button
            onClick={handleConfirm}
            disabled={isLoading}
            className={`
              w-full sm:w-auto px-4 py-2 rounded-md font-medium transition
              disabled:opacity-50 disabled:cursor-not-allowed
              ${getConfirmButtonClass()}
            `}
          >
            {isLoading ? "Procesando..." : confirmText}
          </button>
        </div>
      </div>
    </Modal>
  );
};
